import { getIsUserAttending } from '@shared/utils/connections/calendars'
import { isDayToday, isDayTomorrow } from '@shared/utils/DateUtils'
import { useCallback, useMemo } from 'react'
import { useGlobalState } from '@highlight/ipc/react'
import { CalendarEvent } from '@highlight/common'
import BaseIpcRenderer from '@renderer/ipc/BaseIpc.renderer'

/**
 * Returns all valid calendar events (excludes all-day events and meetings the user isn't attending)
 * Use this for recent activity where you need events across all dates
 */
export const useCalendarEvents = () => {
  // Get calendar events from GlobalState (populated by main process)
  const [allEvents] = useGlobalState<Array<CalendarEvent>>('calendar.todaysEvents', [])
  const [isLoading] = useGlobalState<boolean>('calendar.todaysEventsLoading', false)

  const validEvents = useMemo(() => {
    return allEvents.filter((event) => {
      if (!event.start || !(event.start instanceof Date) || isNaN(event.start.getTime())) {
        console.warn('Invalid event start date:', event)
        return false
      }
      if (event.isAllDay) return false
      if (!getIsUserAttending(event)) return false
      return true
    })
  }, [allEvents])

  return {
    data: validEvents,
    isLoading,
  }
}

/**
 * Returns only today's calendar events (for upcoming meetings, etc.)
 * Wrapper around useCalendarEvents that filters to today only
 */
export const useTodaysCalendarEvents = () => {
  const { data: allEvents, isLoading } = useCalendarEvents()

  const todaysEvents = useMemo(() => {
    return allEvents.filter((event) => isDayToday(event.start))
  }, [allEvents])

  return {
    data: todaysEvents,
    isLoading,
  }
}

/**
 * Returns only tomorrow's calendar events
 * Wrapper around useCalendarEvents that filters to tomorrow only
 */
export const useTomorrowsCalendarEvents = () => {
  const { data: allEvents, isLoading } = useCalendarEvents()

  const tomorrowsEvents = useMemo(() => {
    return allEvents.filter((event) => isDayTomorrow(event.start))
  }, [allEvents])

  return {
    data: tomorrowsEvents,
    isLoading,
  }
}

const BRIEF_GENERATING_TIMEOUT_MS = 3 * 60 * 1000
const briefGeneratingTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

/** Optimistically marks a calendar event as having brief generation in flight.
 *  Written directly onto the event in calendar.todaysEvents so all consumers
 *  read from one place. Main-process polls preserve the flag until brief lands.
 *  Auto-clears after 3 minutes to recover from silent agent failures. */
export const useSetCalendarEventBriefGenerating = () => {
  const [, setAllEvents] = useGlobalState<Array<CalendarEvent>>('calendar.todaysEvents', [])
  return useCallback(
    (eventId: string, generating: boolean) => {
      const existing = briefGeneratingTimeouts.get(eventId)
      if (existing) clearTimeout(existing)
      briefGeneratingTimeouts.delete(eventId)

      setAllEvents((events) => events.map((e) => (e.id === eventId ? { ...e, briefGenerating: generating } : e)))

      if (generating) {
        const timeout = setTimeout(() => {
          briefGeneratingTimeouts.delete(eventId)
          setAllEvents((events) => events.map((e) => (e.id === eventId ? { ...e, briefGenerating: false } : e)))
        }, BRIEF_GENERATING_TIMEOUT_MS)
        briefGeneratingTimeouts.set(eventId, timeout)
      }
    },
    [setAllEvents],
  )
}

export const useInvalidateCalendarEvents = () => {
  // Memoize so consumers using this as a useEffect dep don't re-run
  // every render. The IPC call is stable.
  return useCallback(() => BaseIpcRenderer.refreshCalendarEvents(), [])
}

/** @deprecated Use useInvalidateCalendarEvents instead */
export const useInvalidateTodaysCalendarEvents = useInvalidateCalendarEvents
